import React, { useCallback, useMemo, useState, useRef } from 'react';
import { WeekViewProps, IEvent } from '../core/type';
import { OverlayTrigger } from 'react-bootstrap';
import { isMultiDayEvent as checkIsMultiDayEvent, getEventCategoryColor, getEventStartDateTime, renderTooltip } from '../utils';
import { EventBar } from './eventbar';
import { RootState } from '../../../../../redux/store';
import { useSelector } from 'react-redux';
import { getEventDates, normalizeDate } from '../core/_utility';
import { getSaTodayDate } from '../../../../../utils/date';


interface IEventWithPosition extends IEvent {
    lane: number;
    position: {
        top: string;
        height: string;
        startMinutes: number;
        endMinutes: number;
    };
}
const WeekView: React.FC<WeekViewProps> = ({ currentDate, events, onEventClick, onMoreClick }) => {
    const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
    const titleDisplayedRef = useRef<Set<string>>(new Set());

    const { categoryAndColorsData } = useSelector((state: RootState) => state.eventReducer);

    // Memoized date utilities
    const weekDays = useMemo(() => {
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(startOfWeek);
            d.setDate(startOfWeek.getDate() + i);
            return d;
        });
    }, [currentDate]);

    const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);

    // Reset title tracking when dependencies change
    useMemo(() => {
        titleDisplayedRef.current.clear();
    }, [currentDate, events]);

    // Memoized event utilities
    const isMultiDayEvent = useCallback((event: IEvent) => checkIsMultiDayEvent(event), []);

    const getWeekNumber = useCallback((date: Date) => {
        const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
        const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
        return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
    }, []);

    const getTimeDisplay = useCallback((event: IEvent) => {
        const { start, end } = getEventDates(event);

        if (!start || !end) return '';
        return `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }, []);

    const isToday = useCallback((date: Date) => date.toDateString() === getSaTodayDate().toDateString(), []);

    const toggleExpand = useCallback((key: string) => {
        setExpandedDays(prev => {
            const newSet = new Set(prev);
            newSet.has(key) ? newSet.delete(key) : newSet.add(key);
            return newSet;
        });
    }, []);

    // Get multi-day events with lane assignment
    const getMultiDayEventsForDay = useCallback((day: Date): Array<IEvent & { lane?: number }> => {

        const currentDay = normalizeDate(day);

        const multiDayEvents = events.filter(event => {
            if (!isMultiDayEvent(event)) return false;
            const { start, end } = getEventDates(event);
            const eventStart = normalizeDate(start);
            const eventEnd = normalizeDate(end);
            return currentDay >= eventStart && currentDay <= eventEnd;
        });

        // Sort by duration (longest first) for better lane assignment
        const sortedEvents = [...multiDayEvents].sort((a, b) => {
            const { start: aStart, end: aEnd } = getEventDates(a);
            const { start: bStart, end: bEnd } = getEventDates(b);
            const durationA = aEnd.getTime() - aStart.getTime();
            const durationB =
                bEnd.getTime() -
                bStart.getTime();

            return durationB - durationA;
        });

        const lanes: IEvent[][] = [];
        const eventsWithLanes = sortedEvents.map(event => {
            const laneIndex = lanes.findIndex(lane =>
                !lane.some(e => {
                    const { start: eStart, end: eEnd } = getEventDates(e);
                    const { start: eventStart, end: eventEnd } = getEventDates(event);

                    [eStart, eEnd, eventStart, eventEnd].forEach(date => {
                        if (!date) return;
                        normalizeDate(date)
                    })
                    return eStart <= eventEnd && eventStart <= eEnd;
                })
            );

            if (laneIndex === -1) {
                lanes.push([event]);
                return { ...event, lane: lanes.length - 1 };
            }
            lanes[laneIndex].push(event);
            return { ...event, lane: laneIndex };
        });

        return eventsWithLanes;
    }, [events, isMultiDayEvent]);

    // Get event position in time grid
    const getEventPosition = useCallback((event: IEvent, day: Date) => {

        const { start, end } = getEventDates(event);

        if (!start || !end) return null;

        const dayStart = normalizeDate(day);
        const dayEnd = new Date(day);
        dayEnd.setHours(23, 59, 59, 999);

        const effectiveStart = start > dayStart ? start : dayStart;
        const effectiveEnd = end < dayEnd ? end : dayEnd;

        const startMinutes = effectiveStart.getHours() * 60 + effectiveStart.getMinutes();
        const endMinutes = effectiveEnd.getHours() * 60 + effectiveEnd.getMinutes();
        const durationMinutes = endMinutes - startMinutes;

        return {
            top: `${(startMinutes / 1440) * 100}%`,
            height: `${(durationMinutes / 1440) * 100}%`,
            startMinutes,
            endMinutes
        };
    }, []);

    // Assign lanes for events to prevent overlap
    const assignEventLanes = useCallback((eventsInDay: IEvent[], day: Date) => {
        if (!getEventStartDateTime) return { eventsWithLanes: [], totalLanes: 0 };

        const sortedEvents = [...eventsInDay].sort((a, b) =>
            getEventStartDateTime(a).getTime() - getEventStartDateTime(b).getTime()
        );

        const lanes: IEvent[][] = [];
        const eventsWithLanes = sortedEvents.map(event => {
            const eventPosition = getEventPosition(event, day);
            if (!eventPosition) return { ...event, lane: 0, position: null };

            const laneIndex = lanes.findIndex(lane =>
                !lane.some(e => {
                    const ePosition = getEventPosition(e, day);
                    return ePosition && eventPosition.startMinutes < ePosition.endMinutes &&
                        eventPosition.endMinutes > ePosition.startMinutes;
                })
            );

            const finalLane = laneIndex === -1 ? lanes.push([event]) - 1 : (lanes[laneIndex].push(event), laneIndex);
            return { ...event, lane: finalLane, position: eventPosition };
        }).filter(item => item.position !== null);

        return { eventsWithLanes, totalLanes: lanes.length };
    }, [getEventPosition]);

    const getEventsForDay = useCallback((day: Date): IEvent[] => {
        const currentDay = normalizeDate(day);
        const nextDay = new Date(currentDay);
        nextDay.setDate(nextDay.getDate() + 1);

        return events
            .filter(event => !isMultiDayEvent(event))
            .filter(event => {
                const { start, end } = getEventDates(event)
                return start < nextDay && end > currentDay;
            })
            .sort((a, b) => getEventStartDateTime(a).getTime() - getEventStartDateTime(b).getTime());
    }, [events, isMultiDayEvent]);

    const isMultiHourEvent = useCallback((event: IEvent) => {
        const { start, end } = getEventDates(event);
        if (!start || !end) return false;
        const durationHours = (start.getTime() - end.getTime()) / (1000 * 60 * 60);
        return durationHours > 1;
    }, []);

    // Render event block
    const renderEventBlock = useCallback((event: IEventWithPosition, day: Date, totalLanes: number, index: number) => {
        if (!event.position) return null;

        const lane = event.lane || 0;
        const laneWidth = 100 / totalLanes;
        const isMultiHour = isMultiHourEvent(event);
        const bgColor = `${getEventCategoryColor(event.category, categoryAndColorsData)}25`;
        const borderColor = getEventCategoryColor(event.category, categoryAndColorsData);

        return (
            <OverlayTrigger
                key={`${event.id}-${day.toISOString()}`}
                placement="auto"
                delay={{ show: 250, hide: 400 }}
                overlay={renderTooltip(event, categoryAndColorsData)}
            >
                <div
                    className='day-event-block'
                    style={{
                        position: 'absolute',
                        top: event.position.top,
                        height: `calc(${event.position.height} - 2px)`,
                        left: `${lane * laneWidth}%`,
                        width: `calc(${laneWidth}% - 4px)`,
                        backgroundColor: bgColor,
                        borderLeft: `3px solid ${borderColor}`,
                        borderRadius: '6px',
                        padding: isMultiHour ? '8px 6px' : '4px 6px',
                        cursor: 'pointer',
                        zIndex: 10 + index,
                        overflow: 'hidden',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}
                    onClick={() => onEventClick(day, event)}
                >
                    <div style={{
                        fontWeight: 600,
                        fontSize: isMultiHour ? '12px' : '11px',
                        marginBottom: isMultiHour ? '4px' : '2px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        {event.title}
                    </div>
                    <div style={{ fontSize: isMultiHour ? '10px' : '9px', opacity: 0.8 }}>
                        {getTimeDisplay(event)}
                    </div>
                </div>
            </OverlayTrigger>
        );
    }, [onEventClick, getTimeDisplay, isMultiHourEvent, categoryAndColorsData]);

    // Render day header
    const renderDayHeader = useCallback((day: Date) => {
        const today = isToday(day);
        return (
            <div style={{
                flex: 1,
                textAlign: 'center',
                padding: '12px 16px',
                background: today ? 'linear-gradient(135deg, #eff6ff, #dbeafe)' : 'white',
                borderRight: '1px solid #e5e7eb'
            }}>
                <div style={{ fontWeight: 600, fontSize: '14px' }}>
                    {day.toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
                <div style={{
                    fontSize: '12px',
                    marginTop: '4px',
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    background: today ? '#3b82f6' : '#f3f4f6',
                    color: today ? 'white' : '#4b5563'
                }}>
                    {day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
            </div>
        );
    }, [isToday]);

    const hourLabelStyle = {
        width: '80px',
        flexShrink: 0,
        padding: '12px 16px',
        borderRight: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
        fontWeight: 600,
        color: '#6b7280',
        fontSize: '12px'
    };

    return (
        <div className="overflow-x-auto">
            <div className="week-view" style={{ minWidth: '800px' }}>
                {/* Header */}
                <div className="week-header d-flex border-bottom" style={{ position: 'sticky', top: 0, background: 'white' }}>
                    <div style={hourLabelStyle}>Time / Day</div>
                    <div style={{ display: 'flex', flex: 1 }}>
                        {weekDays.map(day => renderDayHeader(day))}
                    </div>
                </div>

                {/* Multi-Day Events Row */}
                <div className="d-flex border-bottom" style={{ backgroundColor: '#fafafa' }}>
                    <div style={hourLabelStyle}>All Day</div>
                    <div style={{ display: 'flex', flex: 1 }}>
                        {weekDays.map(day => {
                            const multiDayEvents = getMultiDayEventsForDay(day);
                            const isExpanded = expandedDays.has(`${day.toISOString()}-multiday`);
                            const displayEvents = isExpanded ? multiDayEvents : multiDayEvents.slice(0, 3);
                            const maxLane = multiDayEvents.length ? Math.min(Math.max(...multiDayEvents.map(ev => ev.lane || 0)), 3) : -1;
                            const weekNumber = getWeekNumber(day);

                            return (
                                <div key={day.toISOString()} style={{
                                    flex: 1,
                                    padding: '4px',
                                    position: 'relative',
                                    minHeight: '60px',
                                    height: `${Math.max(maxLane * 30, 60)}px`,
                                    borderRight: '1px solid #e5e7eb'
                                }}>
                                    {displayEvents.map(ev => {
                                        const { start, end } = getEventDates(ev)

                                        const eventStart = normalizeDate(start);
                                        const eventEnd = normalizeDate(end);
                                        const currentDay = normalizeDate(day);

                                        return (
                                            <EventBar
                                                key={`${ev.id}-week-${weekNumber}`}
                                                event={ev}
                                                day={day}
                                                lane={ev.lane || 0}
                                                rowHeight={22}
                                                isFirstDay={currentDay.getTime() === eventStart.getTime()}
                                                isLastDay={currentDay.getTime() === eventEnd.getTime()}
                                                titleDisplayedRef={titleDisplayedRef.current}
                                                onEventClick={(e, event) => onEventClick(day, event)}
                                                viewType="week"
                                                currentDate={currentDate}
                                                showArrowIndicators={true}
                                            />
                                        );
                                    })}
                                    {multiDayEvents.length > 3 && !isExpanded && (
                                        <div className='d-flex justify-content-center'>
                                            <button
                                                className='more-btn fs-9 text-primary bg-opacity-0 border-0 fw-semibold p-0'
                                                style={{ position: 'absolute', bottom: '4px', cursor: 'pointer', zIndex: 20 }}
                                                onClick={() => onMoreClick(day)}
                                            >
                                                +{multiDayEvents.length - 3} more
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Time Grid */}
                <div style={{ position: 'relative' }}>
                    {hours.map(hour => (
                        <div key={hour} style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
                            <div style={{
                                width: '80px',
                                flexShrink: 0,
                                padding: '12px 16px',
                                borderRight: '1px solid #e5e7eb',
                                backgroundColor: '#f9fafb',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '80px'
                            }}>
                                <span style={{ fontWeight: 600, fontSize: '12px' }}>{hour.toString().padStart(2, '0')}:00</span>
                                <span style={{ fontSize: '10px', color: '#9ca3af' }}>-</span>
                                <span style={{ fontSize: '10px', color: '#9ca3af' }}>{(hour + 1).toString().padStart(2, '0')}:00</span>
                            </div>
                            <div style={{ display: 'flex', flex: 1 }}>
                                {weekDays.map(day => (
                                    <div key={day.toISOString()} style={{
                                        flex: 1,
                                        minHeight: '80px',
                                        borderRight: '1px solid #e5e7eb',
                                        backgroundColor: 'white',
                                        position: 'relative'
                                    }} />
                                ))}
                            </div>
                        </div>
                    ))}

                    {/* Event Overlays */}
                    <div style={{ position: 'absolute', top: 0, left: '80px', right: 0, bottom: 0, pointerEvents: 'none' }}>
                        {weekDays.map((day, dayIndex) => {
                            const dayEvents = getEventsForDay(day);
                            const { eventsWithLanes, totalLanes } = assignEventLanes(dayEvents, day);
                            const dayKey = `${day.toISOString()}-events`;
                            const isExpanded = expandedDays.has(dayKey);
                            const displayEvents = isExpanded ? eventsWithLanes : eventsWithLanes.slice(0, 5);
                            const hasMore = eventsWithLanes.length > 5;

                            return (
                                <div
                                    key={`overlay-${day.toISOString()}`}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: `${(dayIndex / weekDays.length) * 100}%`,
                                        width: `${(1 / weekDays.length) * 100}%`,
                                        height: '100%',
                                        pointerEvents: 'none'
                                    }}
                                >
                                    <div style={{ position: 'relative', height: '100%', pointerEvents: 'auto' }}>
                                        {displayEvents.map((event, idx) => renderEventBlock(event, day, totalLanes, idx))}
                                        {hasMore && !isExpanded && (
                                            <button
                                                style={{
                                                    position: 'absolute',
                                                    bottom: '8px',
                                                    right: '8px',
                                                    fontSize: '11px',
                                                    padding: '4px 12px',
                                                    background: 'white',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    zIndex: 30,
                                                    fontWeight: 500,
                                                    color: '#4b5563',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                                }}
                                                onClick={() => toggleExpand(dayKey)}
                                            >
                                                +{eventsWithLanes.length - 5} more
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WeekView;